import React, { useEffect, useState } from 'react';
import { Archive, DollarSign, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import { leadsService, paymentsService, projectsService } from '../../services/supabase';
import { navigateToPricing } from '../../utils/navigationUtils';
import { formatStorageUsageFromBytes } from '../../utils/storageQuota';
import { PROJECT_UPDATED_EVENT } from '../../utils/projectEvents';
import { PAYMENT_UPDATED_EVENT } from '../../utils/paymentEvents';
import { LEAD_UPDATED_EVENT } from '../../utils/leadEvents';
import { notifyPaymentMutated } from '../../hooks/usePaymentDueStatusSync';
import UpgradePromptModal from './UpgradePromptModal';

interface QuotaLimitAlertProps {
  isOpen: boolean;
  /** Close / upgrade — discards any pending optimistic create */
  onDismiss: () => void;
  /** After a slot was freed — finalize pending create, then close */
  onSlotFreed: () => void | Promise<void>;
  resource: 'leads' | 'projects' | 'message_templates' | 'storage' | 'payments' | 'calendar_events';
  current: number;
  limit: number;
}

type QuotaSuggestion = {
  id: string;
  name: string;
};

const truncateName = (name: string, max = 28) =>
  name.length > max ? `${name.slice(0, max - 3)}…` : name;

const QuotaLimitAlert: React.FC<QuotaLimitAlertProps> = ({
  isOpen,
  onDismiss,
  onSlotFreed,
  resource,
  current,
  limit,
}) => {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [suggestion, setSuggestion] = useState<QuotaSuggestion | null>(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!isOpen || (resource !== 'projects' && resource !== 'payments' && resource !== 'leads')) {
      setSuggestion(null);
      return;
    }

    let cancelled = false;
    const load =
      resource === 'projects'
        ? projectsService.findArchiveSuggestion()
        : resource === 'payments'
          ? paymentsService.findMarkAsPaidSuggestion()
          : leadsService.findAbandonSuggestion();

    void load.then((next) => {
      if (!cancelled) setSuggestion(next);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, resource]);

  const getResourceName = () => {
    switch (resource) {
      case 'leads':
        return t('settings.usageLeads');
      case 'projects':
        return t('settings.usageProjects');
      case 'message_templates':
        return t('settings.usageEmailTemplates');
      case 'storage':
        return t('settings.usageStorage');
      case 'payments':
        return t('settings.usagePayments');
      case 'calendar_events':
        return t('pricing.featureEvents');
      default:
        return resource;
    }
  };

  const subtitle =
    resource === 'storage'
      ? formatStorageUsageFromBytes(current, limit, language)
      : `${current} / ${limit}`;

  const message =
    resource === 'projects'
      ? t('settings.quotaLimitProjectsMessage')
      : resource === 'payments'
        ? t('settings.quotaLimitPaymentsMessage')
        : resource === 'leads'
          ? t('settings.quotaLimitLeadsMessage')
          : t('settings.quotaLimitSimpleMessage', { resource: getResourceName() });

  const handleArchiveSuggestion = async () => {
    if (!suggestion || acting) return;
    setActing(true);
    try {
      const updated = await projectsService.update(suggestion.id, {
        status: 'ARCHIVED',
      });
      if (!updated) {
        showToast(t('settings.quotaArchiveFailed'), 'error');
        return;
      }
      window.dispatchEvent(
        new CustomEvent(PROJECT_UPDATED_EVENT, { detail: updated })
      );
      await onSlotFreed();
      showToast(
        t('settings.quotaArchiveSuccess', { name: suggestion.name }),
        'success'
      );
    } catch {
      showToast(t('settings.quotaArchiveFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const handleMarkPaidSuggestion = async () => {
    if (!suggestion || acting) return;
    setActing(true);
    try {
      const updated = await paymentsService.markAsPaid(suggestion.id);
      if (!updated) {
        showToast(t('settings.quotaMarkPaidFailed'), 'error');
        return;
      }
      window.dispatchEvent(
        new CustomEvent(PAYMENT_UPDATED_EVENT, { detail: updated })
      );
      notifyPaymentMutated();
      await onSlotFreed();
      showToast(
        t('settings.quotaMarkPaidSuccess', { name: suggestion.name }),
        'success'
      );
    } catch {
      showToast(t('settings.quotaMarkPaidFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const handleAbandonSuggestion = async () => {
    if (!suggestion || acting) return;
    setActing(true);
    try {
      const updated = await leadsService.update(suggestion.id, {
        status: 'abandoned',
        nextActionAt: null,
        lastActionAt: new Date(),
      });
      if (!updated) {
        showToast(t('settings.quotaAbandonFailed'), 'error');
        return;
      }
      window.dispatchEvent(
        new CustomEvent(LEAD_UPDATED_EVENT, { detail: updated })
      );
      await onSlotFreed();
      showToast(
        t('settings.quotaAbandonSuccess', { name: suggestion.name }),
        'success'
      );
    } catch {
      showToast(t('settings.quotaAbandonFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const secondaryAction =
    resource === 'projects' && suggestion
      ? {
          label: t('settings.quotaArchiveNamed', {
            name: truncateName(suggestion.name),
          }),
          onClick: () => {
            void handleArchiveSuggestion();
          },
          loading: acting,
          icon: <Archive className="w-4 h-4" />,
        }
      : resource === 'payments' && suggestion
        ? {
            label: t('settings.quotaMarkPaidNamed', {
              name: truncateName(suggestion.name),
            }),
            onClick: () => {
              void handleMarkPaidSuggestion();
            },
            loading: acting,
            icon: <DollarSign className="w-4 h-4" aria-hidden />,
          }
        : resource === 'leads' && suggestion
          ? {
              label: t('settings.quotaAbandonNamed', {
                name: truncateName(suggestion.name),
              }),
              onClick: () => {
                void handleAbandonSuggestion();
              },
              loading: acting,
              icon: <XCircle className="w-4 h-4" />,
            }
          : undefined;

  return (
    <UpgradePromptModal
      isOpen={isOpen}
      onClose={onDismiss}
      title={t('settings.quotaLimitReached')}
      message={message}
      subtitle={subtitle}
      onUpgrade={() => {
        onDismiss();
        navigateToPricing(navigate);
      }}
      secondaryAction={secondaryAction}
    />
  );
};

export default QuotaLimitAlert;
